/**
 * Fills proposal_template.html placeholders from parsed takeoff text.
 * Used for local compile and to enrich AI output when fields are still blank.
 */

function escapeRegex(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Fill an empty .f span by data-ph, regardless of attribute order. */
export function setField(html, dataPh, value) {
  if (!value) return html;
  const escaped = escapeRegex(dataPh);
  const pattern = new RegExp(
    `<span\\b([^>]*\\bdata-ph="${escaped}"[^>]*)>\\s*<\\/span>`,
    "gi"
  );
  return html.replace(pattern, `<span$1>${value}</span>`);
}

function firstMatch(text, patterns) {
  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) return match[1].trim();
  }
  return "";
}

/** Help/chat text is not a job name. Takeoff facts still parse even if the note is long. */
function looksLikeHelpQuestion(text) {
  const t = (text || "").trim();
  const hasFacts = /\$\s*[\d,]|\d[\d,]*\s*SF\b|spec\s*section|project\s*name\s*:/i.test(t);
  if (hasFacts) return false;
  return /[?]/.test(t) || /\b(why|how to|how do|need help|let me know|what is)\b/i.test(t);
}

function projectNameFromText(text) {
  const labeled = firstMatch(text, [
    /project\s*name\s*:\s*([^\n?]+)/i,
    /(?:^|\n)\s*for\s*:\s*([^\n?]+)/i,
  ]);
  if (labeled && labeled.length <= 80) return labeled;

  const heading = text.match(/^(?!please\b|build\b|hi\b)([^\n?]{2,70}?)\s+(?:BUDGET|PROPOSAL|RFB|BID)\b/im);
  if (heading?.[1]) return heading[1].trim();

  return "";
}

export function parseTakeoffSpecs(text) {
  if (looksLikeHelpQuestion(text)) {
    return {
      projectName: "",
      specSection: "",
      basePrice: "",
      quantity: "",
      system: "",
      thickness: "",
      substrate: "",
      ppBond: "",
      mobilizations: "",
      addMobCost: "",
      salesTax: "",
      salesTaxIncluded: "",
      ppBondIncluded: "",
      dateStr: "",
      scopeNote: "",
    };
  }

  const projectName = projectNameFromText(text);

  const specSection = firstMatch(text, [
    /spec\s*section\s*:\s*(\d+)/i,
    /section\s*(\d{6})/i,
  ]);

  const basePrice = firstMatch(text, [
    /(?:required\s*)?base\s*pric(?:e|ing)\s*:\s*\$?\s*([\d,]+\.?\d*)/i,
    /\$\s*([\d,]+\.?\d*)/,
  ]);

  const quantity = firstMatch(text, [
    /(?:estimated\s*)?([\d,]+)\s*SF\b/i,
    /([\d,]+)\s*SF\s+(?:tapered|of)/i,
  ]);

  const system =
    firstMatch(text, [
      /(lightweight insulating concrete[^.\n]*)/i,
      /(LWIC[^.\n]*)/i,
    ]) ||
    (text.match(/concrete|LWIC|roofing|insulation/i)
      ? "lightweight insulating concrete (LWIC)"
      : "");

  const thickness = firstMatch(text, [/thickness(?:es)?\s*:\s*([^\n]+)/i]);
  const decks = firstMatch(text, [/decks?\s*:\s*([^\n]+)/i]);
  const substrate = decks || firstMatch(text, [/over\s+([^\n,]+)/i]);
  const ppBond = firstMatch(text, [/P\s*&\s*P\s*Bond\s*:\s*\$?\s*([\d,]+\.?\d*)/i]);
  const mobilizations = firstMatch(text, [/mobilizations?\s*:\s*(\d+)/i]);
  const addMobCost = firstMatch(text, [
    /additional\s*mobilization\s*cost\s*:\s*\$?\s*([\d,]+\.?\d*)/i,
  ]);
  const salesTax = firstMatch(text, [
    /sales\s*tax[^:\n]*:\s*(?:not included[^,]*,\s*add if applicable:\s*)?\$?\s*([\d,]+\.?\d*)/i,
  ]);

  const today = new Date();
  const hasFacts = /\$\s*[\d,]|\d[\d,]*\s*SF\b|spec\s*section/i.test(text);
  const dateStr = hasFacts
    ? `${String(today.getMonth() + 1).padStart(2, "0")}/${String(today.getDate()).padStart(2, "0")}/${String(today.getFullYear()).slice(-2)}`
    : "";

  return {
    projectName,
    specSection,
    basePrice,
    quantity,
    system,
    thickness,
    substrate,
    ppBond,
    mobilizations,
    addMobCost,
    salesTax,
    salesTaxIncluded: /sales\s*tax\s*:\s*not\s*included/i.test(text) ? "No" : "",
    ppBondIncluded: ppBond ? "No" : "",
    dateStr,
    scopeNote: [thickness && `Thicknesses: ${thickness}`, decks && `Decks: ${decks}`]
      .filter(Boolean)
      .join("; "),
  };
}

function applyDocType(html, docType) {
  const upper = docType.toUpperCase();
  const heading = upper.includes("ELASTIZELL") || upper.includes("DAILY")
    ? "ELASTIZELL 250 PSI LWIC DAILY REPORT"
    : upper.includes("BUDGET")
    ? "BUDGET PROPOSAL"
    : upper.includes("CHANGE")
      ? "CHANGE ORDER REQUEST"
      : upper.includes("REPORT")
        ? "PROJECT REPORT"
        : "PROJECT PROPOSAL";

  return html
    .replace(/(<div class="t1">)[^<]+(<\/div>)/, `$1${heading}$2`)
    .replace(
      /(<title>Nettles Construction Solutions — )[^<]+(<\/title>)/,
      `$1${heading}$2`
    );
}

/** Apply parsed specs onto any HTML that still has empty data-ph fields. */
export function applySpecsToHtml(html, specs, docType = "Proposal") {
  // Only force Nettles doc-type heading on the house template, not matched layouts
  let out = /class="t1"|BUDGET PROPOSAL|PROJECT PROPOSAL|CHANGE ORDER REQUEST|ELASTIZELL 250 PSI LWIC DAILY REPORT/i.test(html)
    ? applyDocType(html, docType)
    : html;

  out = setField(out, "project name", specs.projectName);
  out = setField(out, "building name", specs.projectName);
  out = setField(out, "project / subject", specs.projectName);
  out = setField(out, "000000", specs.specSection);
  out = setField(out, "spec section title", specs.specSection ? `Section ${specs.specSection}` : "");
  out = setField(out, "base price", specs.basePrice);
  out = setField(out, "total cost", specs.basePrice ? `$${specs.basePrice}` : "");
  out = setField(out, "total quantity", specs.quantity);
  out = setField(out, "unit", specs.quantity ? "SF" : "");
  out = setField(out, "system / assembly", specs.system);
  out = setField(out, "substrate", specs.substrate);
  out = setField(
    out,
    "system, thickness",
    [specs.system, specs.thickness].filter(Boolean).join(", ")
  );
  out = setField(out, "qty", specs.quantity);
  out = setField(out, "MM/DD/YY", specs.dateStr);
  out = setField(out, "work by others", specs.scopeNote);

  // First empty "count" → mobilizations
  if (specs.mobilizations) {
    out = out.replace(
      /(<span\b[^>]*\bdata-ph="count"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.mobilizations}$2`
    );
  }

  // Targeted money rows
  if (specs.ppBond) {
    out = out.replace(
      /(Add for P&amp;P Bond:[\s\S]*?<span\b[^>]*\bdata-ph="amount"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.ppBond}$2`
    );
  }
  if (specs.addMobCost) {
    out = out.replace(
      /(Cost per Additional Mobilization:[\s\S]*?<span\b[^>]*\bdata-ph="amount"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.addMobCost}$2`
    );
  }
  if (specs.salesTax) {
    out = out.replace(
      /(Add if Sales Tax is Applicable:[\s\S]*?<span\b[^>]*\bdata-ph="amount"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.salesTax}$2`
    );
  }

  // First empty amount in price table / base price block
  if (specs.basePrice) {
    out = setField(out, "amount", specs.basePrice);
  }

  if (specs.salesTaxIncluded) {
    out = out.replace(
      /(Sales Tax Included:[\s\S]*?<span\b[^>]*\bdata-ph="Yes\/No"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.salesTaxIncluded}$2`
    );
  }
  if (specs.ppBondIncluded) {
    out = out.replace(
      /(P&amp;P Bond Included:[\s\S]*?<span\b[^>]*\bdata-ph="Yes\/No"[^>]*>)\s*(<\/span>)/i,
      `$1${specs.ppBondIncluded}$2`
    );
  }

  return out;
}

/** Prices, quantities, or labeled job fields — enough to fill a document. */
export function hasTakeoffFacts(text) {
  return /\$\s*[\d,]|\d[\d,]*\s*SF\b|spec\s*section\s*:|project\s*name\s*:|base\s*pric/i.test(text || "");
}

/** Build only when the user asks for a document or pastes takeoff facts. Chat stays conversational otherwise. */
export function wantsDocumentBuild(text) {
  const t = (text || "").trim();
  if (!t) return false;
  if (hasTakeoffFacts(t)) return true;
  return (
    /\b(build|generate|draft|fill|compile)\b/i.test(t) &&
    /\b(proposal|template|bid|quote|document|html|report|it|now|blank)\b/i.test(t)
  );
}

export function wantsConstructionBidLayout(text) {
  return /construction[-\s]?standard|construction bid|blue title/i.test(text || "");
}

export function countFilledFields(html) {
  const empty = (html.match(/data-ph="[^"]*">\s*<\/span>/gi) || []).length;
  const total = (html.match(/data-ph="/gi) || []).length;
  return { empty, total, filled: Math.max(0, total - empty) };
}

export function extractHtmlFromResponse(responseText) {
  if (!responseText) return null;

  const fenced = responseText.match(/```(?:html)?\s*([\s\S]*?)```/i);
  if (fenced) {
    const body = fenced[1].trim();
    if (
      body.includes("<html") ||
      body.includes("<!DOCTYPE") ||
      body.includes("<div") ||
      body.includes("<h1") ||
      body.includes("data-ph") ||
      body.includes('class="page"') ||
      body.includes("class='page'")
    ) {
      return body;
    }
  }

  const start = responseText.search(
    /(?:<!DOCTYPE|<html[\s>]|<meta charset|<div class="bar"|<div class="page"|<h1[\s>])/i
  );
  if (start >= 0) {
    let html = responseText.slice(start);
    const endFence = html.search(/\n```/);
    if (endFence > 100) html = html.slice(0, endFence);
    return html.trim();
  }

  return null;
}

function blankField(placeholder) {
  return `<span class="f" data-ph="${placeholder}"></span>`;
}

/** Blank Elastizell daily report, shown in the side preview before any data is entered. */
export function buildDailyReportSampleHtml() {
  const row = (label) =>
    `<div class="field"><div class="lbl">${label}</div><div class="val">${blankField(label)}</div></div>`;
  const samples = [1, 2, 3, 4]
    .map(
      (n) =>
        `<div class="field"><div class="lbl">Sample ${n}</div><div class="val">${blankField(`Sample ${n} weight, time, density`)}</div></div>`
    )
    .join("");

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Elastizell 250 PSI LWIC Daily Report</title>
<style>
  body { margin: 0; background: #f5f5f4; color: #1c1917; font: 12px/1.4 "Segoe UI", sans-serif; }
  .page { max-width: 8.5in; margin: 16px auto; background: #fff; padding: 0.55in; box-shadow: 0 8px 24px rgba(0,0,0,.06); }
  h1 { margin: 0; font-size: 16px; }
  .sub { margin-top: 4px; color: #78716c; font-size: 11px; }
  h2 { margin: 16px 0 8px; font-size: 12px; letter-spacing: .04em; text-transform: uppercase; color: #b91c1c; border-bottom: 1px solid #e7e5e4; padding-bottom: 4px; }
  .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 14px; }
  .field { break-inside: avoid; }
  .lbl { font-size: 10px; font-weight: 700; color: #78716c; text-transform: uppercase; letter-spacing: .03em; }
  .val { margin-top: 2px; min-height: 22px; border: 1px solid #d6d3d1; border-radius: 4px; padding: 3px 6px; }
  .val.tall { min-height: 48px; }
  .f:empty::before { content: attr(data-ph); color: #a8a29e; }
</style>
</head>
<body>
<article class="page">
  <h1>Elastizell 250 PSI LWIC Daily Report</h1>
  <div class="sub">Blank sample · fields stay empty until a report is filled</div>
  <h2>Project</h2>
  <div class="grid">
    ${row("Project Name")}
    ${row("Project Address")}
    ${row("Project No.")}
    ${row("Prepared By")}
    ${row("Customer / GC")}
    ${row("Report Date")}
  </div>
  <div class="field" style="margin-top:8px"><div class="lbl">Scope Description</div><div class="val tall">${blankField("Scope description")}</div></div>
  <h2>Production Summary</h2>
  <div class="grid">
    ${row("EPS Slurried Today (SF)")}
    ${row("EPS Slurried to Date (SF)")}
    ${row("Total Slurry (SF)")}
    ${row("EPS % Complete")}
    ${row("Top Coat Installed Today (SF)")}
    ${row("Top Coat Installed to Date (SF)")}
    ${row("Total Top Coat (SF)")}
    ${row("Top Coat % Complete")}
  </div>
  <h2>Mix Design / QC</h2>
  ${row("Bucket volume (cu ft)")}
  ${samples}
  ${row("Average Wet Density (pcf)")}
  <div class="field"><div class="lbl">Mix Adjustments / QC Notes</div><div class="val tall">${blankField("QC notes")}</div></div>
  <h2>Daily Notes &amp; Planning</h2>
  <div class="field"><div class="lbl">Weather</div><div class="val tall">${blankField("Weather")}</div></div>
  <div class="grid">
    ${row("Coordination / Planning")}
    ${row("Equipment / Downtime")}
    ${row("Material / Deliveries / Stock")}
  </div>
  <div class="grid" style="margin-top:8px">
    <div class="field"><div class="lbl">Today's Summary</div><div class="val tall">${blankField("Today's summary")}</div></div>
    <div class="field"><div class="lbl">Tomorrow's Plan</div><div class="val tall">${blankField("Tomorrow's plan")}</div></div>
  </div>
  <h2>Sign-off</h2>
  <div class="grid">
    ${row("Supervisor Signature")}
    ${row("Sign-off Date")}
  </div>
</article>
</body>
</html>`;
}

export function compileProposalLocally(templateHtml, userText, docType = "Proposal") {
  const specs = parseTakeoffSpecs(userText);
  const html = applySpecsToHtml(templateHtml, specs, docType);
  const { filled } = countFilledFields(html);

  const notes = [];
  if (specs.projectName) notes.push(`Project: ${specs.projectName}`);
  if (specs.basePrice) notes.push(`Base price: $${specs.basePrice}`);
  if (specs.quantity) notes.push(`Quantity: ${specs.quantity} SF`);
  if (specs.specSection) notes.push(`Spec Section ${specs.specSection}`);
  notes.push(`Filled ${filled} template field(s) from your prompt.`);
  if (filled === 0) {
    notes.push(
      "Could not parse takeoff fields — include project name, SF, Spec Section, and Base Pricing."
    );
  }

  return { html, specs, reviewerNotes: notes };
}
